import * as React from "react";
import { useCallback, useMemo } from "react";
import { Formik } from "formik";
import styled, { css } from "styled-components";
import moment from "moment";
import { Button } from "components/bootstrap";
import Popover from "components/common/Popover";
import { Icon, KeyCapture, ModalSubmit, NestedForm } from "components/common";
import { isTimeRange, isTypeRelative } from "views/typeGuards/timeRange";
import { normalizeFromPickerForSearchBar } from "views/logic/queries/NormalizeTimeRange";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import { classifyRelativeTimeRange } from "./RelativeTimeRangeClassifiedHelper";
import TimeRangePickerFormContent, { allTimeRangeTypes } from "./TimeRangePickerFormContent";
import useTimeRangeValidation from "./useTimeRangeValidation";
const PopoverTitle = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;

  > span {
    font-weight: 600;
  }
`;
const LimitLabel = styled.span(
  ({ theme }) => css`
    > svg {
      margin-right: 3px;
      color: ${theme.colors.variant.dark.warning};
    }

    > span {
      font-size: ${theme.fonts.size.small};
      color: ${theme.colors.variant.darkest.warning};
    }
  `
);
const initialFormValues = (currentTimeRange) => {
  if (isTimeRange(currentTimeRange)) {
    return {
      timeRangeTabs: {
        [currentTimeRange.type]: isTypeRelative(currentTimeRange) ? classifyRelativeTimeRange(currentTimeRange) : currentTimeRange
      },
      activeTab: currentTimeRange.type
    };
  }
  return {
    timeRangeTabs: {},
    activeTab: void 0
  };
};
const TimeRangePicker = ({
  children = void 0,
  show,
  noOverride = false,
  toggleDropdownShow,
  currentTimeRange,
  setCurrentTimeRange,
  validTypes = allTimeRangeTypes,
  position,
  limitDuration,
  withinPortal = true
}) => {
  const sendTelemetry = useSendTelemetry();
  const validateTimeRange = useTimeRangeValidation(limitDuration);
  const handleNoOverride = useCallback(() => {
    setCurrentTimeRange({});
    toggleDropdownShow();
  }, [setCurrentTimeRange, toggleDropdownShow]);
  const handleCancel = useCallback(() => {
    toggleDropdownShow();
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_TIMERANGE_PICKER_CANCELED, {
      app_section: "search-bar",
      app_action_value: "search-time-range-cancel-button"
    });
  }, [sendTelemetry, toggleDropdownShow]);
  const onSubmit = useCallback(
    ({ timeRangeTabs, activeTab }) => {
      const normalizedTimeRange = normalizeFromPickerForSearchBar(timeRangeTabs[activeTab]);
      setCurrentTimeRange(normalizedTimeRange);
      toggleDropdownShow();
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_TIMERANGE_PICKER_UPDATED, {
        app_section: "search-bar",
        app_action_value: "search-time-range-confirm-button",
        event_details: {
          timerange: normalizedTimeRange
        }
      });
    },
    [sendTelemetry, setCurrentTimeRange, toggleDropdownShow]
  );
  const title = /* @__PURE__ */ React.createElement(PopoverTitle, null, /* @__PURE__ */ React.createElement("span", null, "Search Time Range"), limitDuration > 0 && /* @__PURE__ */ React.createElement(LimitLabel, null, /* @__PURE__ */ React.createElement(Icon, { name: "warning" }), /* @__PURE__ */ React.createElement("span", null, "Admin has limited searching to ", moment.duration(-limitDuration, "seconds").humanize(true))));
  const initialValues = useMemo(() => initialFormValues(currentTimeRange), [currentTimeRange]);
  return /* @__PURE__ */ React.createElement(
    Popover,
    {
      id: "timerange-type",
      "data-testid": "timerange-type",
      opened: show,
      onChange: toggleDropdownShow,
      position,
      withinPortal,
      withArrow: true,
      width: 735,
      zIndex: 1060
    },
    /* @__PURE__ */ React.createElement(Popover.Target, null, children),
    /* @__PURE__ */ React.createElement(Popover.Dropdown, { title }, /* @__PURE__ */ React.createElement(
      Formik,
      {
        initialValues,
        validate: validateTimeRange,
        onSubmit,
        validateOnMount: true
      },
      ({ isValid, submitForm, isValidating }) => /* @__PURE__ */ React.createElement(
        KeyCapture,
        {
          shortcuts: [
            {
              actionKey: "submit-form",
              callback: submitForm,
              scope: "general",
              options: { displayInOverview: false }
            },
            {
              actionKey: "close-modal",
              callback: handleCancel,
              scope: "general",
              options: { displayInOverview: false }
            }
          ]
        },
        /* @__PURE__ */ React.createElement(NestedForm, null, /* @__PURE__ */ React.createElement(TimeRangePickerFormContent, { limitDuration, validTypes }, /* @__PURE__ */ React.createElement(
          ModalSubmit,
          {
            leftCol: noOverride && /* @__PURE__ */ React.createElement(Button, { bsStyle: "link", onClick: handleNoOverride }, "No Override"),
            onCancel: handleCancel,
            disabledSubmit: !isValid || isValidating,
            submitButtonText: "Update time range"
          }
        )))
      )
    ))
  );
};
export default TimeRangePicker;
